'use client'

import { useCallback, useEffect, useState } from 'react'
import { Link } from '@/shared/navigation'
import { confirmReturnPickup } from '@/features/delman/api/confirmReturnPickup'
import { delmanTerms } from '@/features/delman/delmanTerms'
import {
  getOrCreateTodayTrip,
  saveTripRecord,
} from '@/features/delman/storage/tripStorage'
import { loadTodayTripPreferApi } from '@/features/delman/api/delmanTripApi'
import { getDelmanDevice } from '@/shared/auth/delmanDeviceStorage'
import { PinKeypadModal } from '@/shared/components/PinKeypadModal'
import { QrCameraScanner } from '@/shared/components/QrCameraScanner'
import { Toast } from '@/shared/components/Toast'
import { consumeLastSyncQueued } from '@/shared/sync/kitchenSync'
import { useClientHydrated } from '@/shared/hooks/useClientHydrated'
import { parseReturnPickupQrPayload } from '@/shared/utils/qrHandoverPayload'

export function DelmanPickupReturnPage() {
  const hydrated = useClientHydrated()
  const device = hydrated ? getDelmanDevice() : null
  const delmanName = device?.delmanName ?? 'Delman'
  const kitchenName = device?.kitchenName ?? 'Dapur'

  const [trip, setTrip] = useState(() => getOrCreateTodayTrip(delmanName, kitchenName))
  const [scanInput, setScanInput] = useState('')
  const [selectedReturnId, setSelectedReturnId] = useState<string | null>(null)
  const [pinOpen, setPinOpen] = useState(false)
  const [toast, setToast] = useState<string | null>(null)
  const [cameraActive, setCameraActive] = useState(true)
  const [manualOpen, setManualOpen] = useState(false)

  const dismissToast = useCallback(() => setToast(null), [])
  const showToast = useCallback((message: string) => setToast(message), [])

  useEffect(() => {
    if (!hydrated || !device) return
    let cancelled = false
    void loadTodayTripPreferApi({
      delmanId: device.delmanId,
      delmanName: device.delmanName,
      kitchenName: device.kitchenName,
    }).then((loaded) => {
      if (!cancelled) setTrip(loaded)
    })
    return () => {
      cancelled = true
    }
  }, [hydrated, device?.delmanId, device?.delmanName, device?.kitchenName])

  const tripStarted = trip.status === 'in_progress' || trip.status === 'completed'
  const pending = trip.returnPickups.filter((r) => r.status === 'waiting_scan')
  const picked = trip.returnPickups.filter((r) => r.status === 'picked_up')

  function findPendingMatch(payload: string) {
    const parsed = parseReturnPickupQrPayload(payload)
    if (!parsed) return undefined
    const inList = pending.find((r) => r.id.toLowerCase() === parsed.returnId.toLowerCase())
    if (inList) return inList
    // QR valid dari app toko tapi retur belum ter-link ke trip (delivery_trip_id NULL).
    return {
      id: parsed.returnId,
      returnCode: parsed.returnId.slice(0, 8).toUpperCase(),
      storeName: 'Toko',
      itemCount: 0,
      destination: 'to_kitchen' as const,
      status: 'waiting_scan' as const,
      lines: [],
    }
  }

  function beginPickup(matchId: string, payload: string) {
    setScanInput(payload.trim())
    setSelectedReturnId(matchId)
    setCameraActive(false)
    setPinOpen(true)
  }

  function handleScanSubmit() {
    const payload = scanInput.trim()
    if (!payload) {
      showToast(delmanTerms.returnPickup.emptyScan)
      return
    }

    const match = findPendingMatch(payload)
    if (!match) {
      showToast(delmanTerms.returnPickup.unknownQr)
      return
    }

    beginPickup(match.id, payload)
  }

  const handleCameraScan = useCallback(
    (value: string) => {
      if (!tripStarted) return
      const match = findPendingMatch(value)
      if (!match) {
        showToast(delmanTerms.returnPickup.unknownQr)
        return
      }
      showToast(delmanTerms.returnPickup.scanned(match.id))
      beginPickup(match.id, value)
    },
    [tripStarted, pending, showToast],
  )

  function selectPending(docId: string) {
    if (!tripStarted) {
      showToast(delmanTerms.returnPickup.tripNotStarted)
      return
    }
    const doc = pending.find((r) => r.id === docId)
    showToast(
      doc
        ? `Scan QR toko untuk ${doc.returnCode} (SUPEROTI:RP:…)`
        : delmanTerms.returnPickup.emptyScan,
    )
  }

  async function confirmPickup(pin: string) {
    if (!device || !selectedReturnId) {
      throw new Error('Data pickup tidak lengkap.')
    }

    await confirmReturnPickup({
      returnId: selectedReturnId,
      deviceId: device.deviceId,
      pin,
      qrPayload: scanInput.trim(),
    })

    const queued = consumeLastSyncQueued() ? ` ${delmanTerms.sync.queued}` : ''
    const pickedAt = new Intl.DateTimeFormat('id-ID', {
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date())

    const nextTrip = {
      ...trip,
      returnPickups: trip.returnPickups.map((r) =>
        r.id === selectedReturnId
          ? { ...r, status: 'picked_up' as const, pickedUpAt: pickedAt }
          : r,
      ),
    }
    saveTripRecord(nextTrip)
    setTrip(nextTrip)
    setPinOpen(false)
    setSelectedReturnId(null)
    setScanInput('')
    setCameraActive(true)
    showToast(`${delmanTerms.returnPickup.successToast}${queued}`)
  }

  const selectedReturn = trip.returnPickups.find((r) => r.id === selectedReturnId)

  if (!hydrated) {
    return <div className="min-h-[200px]" aria-hidden />
  }

  return (
    <section className="space-y-4">
      <div className="card">
        <p className="text-headline-md text-primary">{delmanTerms.modules.pickup}</p>
        <p className="text-body-sm text-on-surface-variant mt-1">{delmanTerms.returnPickup.intro}</p>
      </div>

      {!tripStarted && (
        <div className="card flex items-start gap-2 border-status-warning/40 bg-status-warning-bg/30">
          <span className="material-symbols-outlined text-status-warning shrink-0">info</span>
          <p className="text-body-sm text-status-warning">{delmanTerms.returnPickup.tripNotStarted}</p>
        </div>
      )}

      <div className="card space-y-3">
        <div className="flex items-center justify-between gap-2">
          <p className="text-body-sm font-semibold">{delmanTerms.returnPickup.scan}</p>
          {tripStarted && cameraActive && !pinOpen && (
            <span className="badge-warning text-label-md">Live</span>
          )}
        </div>
        <QrCameraScanner
          active={tripStarted && cameraActive && !pinOpen}
          inactiveMessage={
            !tripStarted
              ? delmanTerms.returnPickup.cameraInactiveTrip
              : pinOpen
                ? 'Konfirmasi PIN untuk menyelesaikan pickup.'
                : delmanTerms.returnPickup.cameraHint
          }
          onScan={handleCameraScan}
          unsupportedMessage={delmanTerms.returnPickup.cameraUnsupported}
          permissionDeniedMessage={delmanTerms.returnPickup.cameraPermissionDenied}
          scanningHint={delmanTerms.returnPickup.cameraHint}
        />

        <details
          className="rounded-lg border border-outline-variant/60 bg-surface-container-low"
          open={manualOpen}
          onToggle={(e) => setManualOpen((e.target as HTMLDetailsElement).open)}
        >
          <summary className="cursor-pointer select-none px-3 py-3 text-body-sm font-medium min-h-[44px] flex items-center gap-2">
            <span className="material-symbols-outlined text-lg text-on-surface-variant">keyboard</span>
            {delmanTerms.returnPickup.manualToggle}
          </summary>
          <div className="space-y-3 border-t border-outline-variant/40 px-3 pb-3 pt-2">
            <label className="field-label" htmlFor="return_qr_payload">
              {delmanTerms.returnPickup.pasteLabel}
            </label>
            <input
              id="return_qr_payload"
              className="input-field font-mono"
              placeholder={delmanTerms.returnPickup.pastePlaceholder}
              value={scanInput}
              onChange={(e) => setScanInput(e.target.value)}
              autoComplete="off"
              spellCheck={false}
              disabled={!tripStarted}
            />
            <button
              type="button"
              className="btn-primary w-full min-h-[48px] btn-tactile disabled:opacity-40"
              disabled={!tripStarted || !scanInput.trim()}
              onClick={handleScanSubmit}
            >
              {delmanTerms.returnPickup.confirm}
            </button>
          </div>
        </details>
      </div>

      {pending.length > 0 && (
        <div className="card space-y-2">
          <p className="text-body-sm font-semibold text-status-warning">
            {delmanTerms.returnPickup.pendingTitle(pending.length)}
          </p>
          {pending.map((doc) => (
            <button
              key={doc.id}
              type="button"
              onClick={() => selectPending(doc.id)}
              disabled={!tripStarted}
              className="w-full text-left rounded-lg border border-status-warning/30 bg-status-warning-bg/20 p-3 text-body-sm min-h-[44px] hover:border-status-warning/50 transition-colors disabled:opacity-50"
            >
              <p className="font-mono text-label-md">{doc.returnCode}</p>
              <p className="mt-1">
                {doc.storeName} · {doc.itemCount} item · ke dapur
              </p>
              {doc.lines.length > 0 && (
                <ul className="mt-2 space-y-1 border-t border-status-warning/20 pt-2">
                  {doc.lines.map((line, idx) => (
                    <li
                      key={`${line.productName}-${line.productionDate}-${idx}`}
                      className="flex items-center justify-between gap-2 text-label-md"
                    >
                      <span className="text-on-surface">{line.productName}</span>
                      <span className="font-mono text-on-surface-variant shrink-0">
                        {line.qty} pcs · {line.productionDate}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </button>
          ))}
        </div>
      )}

      {picked.length > 0 && (
        <div className="card space-y-2">
          <p className="text-body-sm font-semibold text-status-success">{delmanTerms.returnPickup.pickedTitle}</p>
          {picked.map((doc) => (
            <div key={doc.id} className="rounded-lg bg-surface-container p-3 text-body-sm opacity-80">
              <p className="font-mono text-label-md">{doc.returnCode}</p>
              <p className="mt-1">
                {doc.storeName} · {doc.pickedUpAt}
              </p>
              {doc.lines.length > 0 && (
                <ul className="mt-2 space-y-1 border-t border-outline-variant/40 pt-2">
                  {doc.lines.map((line, idx) => (
                    <li
                      key={`${line.productName}-${line.productionDate}-${idx}`}
                      className="flex items-center justify-between gap-2 text-label-md"
                    >
                      <span>{line.productName}</span>
                      <span className="font-mono shrink-0">
                        {line.qty} pcs · {line.productionDate}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </div>
      )}

      <Link
        href="/delman/trip"
        className="btn-outline block text-center min-h-[44px] leading-[44px]"
      >
        {delmanTerms.returnPickup.backToTrip}
      </Link>

      <PinKeypadModal
        open={pinOpen}
        title={delmanTerms.returnPickup.pinTitle}
        description={
          selectedReturn
            ? delmanTerms.returnPickup.pinDescription(
                selectedReturn.returnCode,
                selectedReturn.itemCount,
                selectedReturn.storeName,
              )
            : 'Masukkan PIN delman.'
        }
        confirmLabel={delmanTerms.returnPickup.confirm}
        onClose={() => {
          setPinOpen(false)
          setSelectedReturnId(null)
          setCameraActive(true)
        }}
        onConfirm={confirmPickup}
      />

      {toast && <Toast message={toast} onDismiss={dismissToast} />}
    </section>
  )
}
